import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  APP_INITIALIZER,
  inject,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth, connectAuthEmulator } from '@angular/fire/auth';
import { provideFirestore, getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { AutoLoginService } from './core/services/auto-login.service';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { cacheInterceptor } from './core/interceptors/cache.interceptor';
import { retryInterceptor } from './core/interceptors/retry.interceptor';
import { provideServiceWorker } from '@angular/service-worker';

function isLocalHost(hostname: string): boolean {
  if (['localhost', '127.0.0.1', '::1'].includes(hostname)) return true;
  if (hostname.startsWith('10.')) return true;
  if (hostname.startsWith('192.168.')) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)) return true;
  if (/^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(hostname)) return true;
  return false;
}

function shouldUseFirebaseEmulators(): boolean {
  if (!(environment.deploymentEnv === 'local' && environment.useEmulators)) {
    return false;
  }

  try {
    const apiUrl = new URL(environment.api.baseUrl);
    return isLocalHost(apiUrl.hostname);
  } catch {
    return false;
  }
}

const enableFirebaseEmulators = shouldUseFirebaseEmulators();

if (environment.useEmulators && !enableFirebaseEmulators) {
  console.warn('[Firebase] Emulator mode disabled because API baseUrl is remote');
}

/**
 * Factory function for APP_INITIALIZER to run auto-login
 * This runs before the app renders, ensuring auth state is ready
 */
function initializeAuth(): () => Promise<void> {
  const autoLoginService = inject(AutoLoginService);
  return async () => {
    await autoLoginService.tryAutoLogin();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([cacheInterceptor, authInterceptor, retryInterceptor])),
    // Firebase providers
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (enableFirebaseEmulators) {
        const emulatorHost =
          typeof window !== 'undefined' && window.location?.hostname
            ? window.location.hostname
            : '127.0.0.1';
        const authPort = environment.emulatorPorts?.auth ?? 9099;
        const authUrl = `http://${emulatorHost}:${authPort}`;
        connectAuthEmulator(auth, authUrl, { disableWarnings: true });
        console.log(`[DEV] Connected to Firebase Auth Emulator: ${authUrl}`);
      }
      return auth;
    }),
    provideFirestore(() => {
      // Get Firestore instance with named database if specified
      const firestore = environment.firebase.databaseId
        ? getFirestore(initializeApp(environment.firebase), environment.firebase.databaseId)
        : getFirestore();

      // Log which database we're connecting to
      if (environment.firebase.databaseId) {
        console.log(
          `[Firebase] Connected to Firestore database: ${environment.firebase.databaseId}`,
        );
      }

      // Connect to emulator if enabled
      if (enableFirebaseEmulators) {
        const emulatorHost =
          typeof window !== 'undefined' && window.location?.hostname
            ? window.location.hostname
            : '127.0.0.1';
        const fsPort = environment.emulatorPorts?.firestore ?? 8080;
        connectFirestoreEmulator(firestore, emulatorHost, fsPort);
        console.log(`[DEV] Connected to Firestore Emulator: ${emulatorHost}:${fsPort}`);
      }
      return firestore;
    }),
    // Remote Config provider
    provideRemoteConfig(() => {
      const remoteConfig = getRemoteConfig();
      // Configure fetch timeout and minimum fetch interval
      remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
      remoteConfig.settings.fetchTimeoutMillis = 60000; // 60 seconds
      console.log('[Firebase] Remote Config initialized');
      return remoteConfig;
    }),
    // Auto-login initializer (runs before app renders)
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
